/** @flow */
import React from 'react';
import { inject, observer } from 'mobx-react';
import { observable, computed, autorun } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Moment from 'react-moment';

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import AssetStore from 'stores/AssetStore';
import { AssetComment } from 'models/Assets';
import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import Loading from 'components/Loading/Loading';
import UltimatePaginationMUI from 'components/UltimatePaginationMUI/UltimatePaginationMUI';

import styles from './AssetNotepad.module.scss';

type InjectedProps = {
  assetStore: AssetStore,
} & TranslatorProps;

@translate()
@inject('assetStore')
@observer
export default class AssetNotepad extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  @observable newCommentText: string;

  @observable pageNumber = 1;

  @computed get canSubmit(): boolean {
    return !!this.newCommentText && !this.props.assetStore.loadingNewAssetComment;
  }

  componentDidMount() {
    const { assetStore } = this.props;

    autorun(() => {
      if (assetStore.asset && assetStore.asset.id) {
        this.loadAssetComments();
      }
    });
  }

  _firstRun = true;
  loadAssetComments = async () => {
    const { assetStore } = this.props;
    await assetStore.loadAssetComments(assetStore.asset.id, this.pageNumber, !this._firstRun);

    this._firstRun = false;
  };

  handleNewCommentTextChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    this.newCommentText = event.nativeEvent.target.value;
  };

  handleNewCommentSubmitButtonClick = async () => {
    const { assetStore } = this.props;

    await assetStore.addAssetComment(assetStore.asset.id, this.newCommentText);
    this.loadAssetComments();
    this.newCommentText = '';
  };

  handlePaginationChange = (newPageNumber: number) => {
    this.pageNumber = newPageNumber;
  };

  renderCommentsTable = () => {
    const { assetStore, t } = this.props;

    if (assetStore.loadingAssetComments) {
      return <Loading />;
    }

    return (
      <table className="layout-table">
        <thead>
          <tr>
            <th>{t('general:assets.notepad.labels.date')}</th>
            <th>{t('general:assets.notepad.labels.time')}</th>
            <th>{t('general:assets.notepad.labels.comment')}</th>
            <th>{t('general:assets.notepad.labels.added_by')}</th>
          </tr>
        </thead>

        {!!assetStore.assetComments.data && (
          <tbody>{assetStore.assetComments.data.map(this.renderComment)}</tbody>
        )}
      </table>
    );
  };

  renderComment = (entry: AssetComment, index: number) => (
    <tr key={index}>
      <td>
        <Moment date={entry.created_at} format="MMMM DD, YYYY" />
      </td>
      <td>
        <Moment date={entry.created_at} format="HH:mm:ss" />
      </td>
      <td>{entry.comment}</td>
      <td>{entry.user}</td>
    </tr>
  );

  renderNewCommentField = () => (
    <section className={styles.newCommentSection}>
      <ElectroTextField
        field={{ bind: () => {}, value: this.newCommentText }}
        props={{
          onChange: this.handleNewCommentTextChange,
          placeholder: this.props.t('general:assets.notepad.placeholders.new_comment'),
        }}
      />

      {this.props.assetStore.loadingNewAssetComment && (
        <Loading variant="small" style={{ width: 'auto', marginLeft: 16 }} />
      )}

      <Button
        className={styles.submitButton}
        variant="contained"
        color="primary"
        disabled={!this.canSubmit}
        onClick={this.handleNewCommentSubmitButtonClick}
      >
        {this.props.t('general:assets.notepad.labels.submit')}
      </Button>
    </section>
  );

  renderPaginationControl = () => {
    const {
      assetStore: { assetComments },
    } = this.props;

    const totalPages = assetComments && assetComments.last_page ? assetComments.last_page : 1;
    const currentPage =
      assetComments && assetComments.current_page === 'number'
        ? assetComments.current_page
        : this.pageNumber;

    return (
      <div className={styles.paginationWrapper}>
        <UltimatePaginationMUI
          totalPages={totalPages}
          currentPage={currentPage}
          onChange={this.handlePaginationChange}
        />
      </div>
    );
  };

  render() {
    const { t } = this.props;

    return (
      <div className={styles.assetNotepad}>
        <h3>{t('general:assets.notepad.title')}</h3>

        {this.renderNewCommentField()}

        {this.renderCommentsTable()}
        {this.renderPaginationControl()}
      </div>
    );
  }
}
